/**
 * Audio Player
 *
 * Composable audio playback controls for chat audio and generated speech.
 *
 * Primary responsibilities:
 * - Wrap native audio with explicit Losi-styled controls
 * - Support remote URLs and base64 speech audio data
 * - Expose granular controls for compact chat playback surfaces
 *
 * Dependencies:
 * - lucide-react
 * - lib/utils
 */

'use client'

import {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentProps,
  type CSSProperties,
  type MutableRefObject
} from 'react'
import {
  Pause,
  RotateCcw,
  RotateCw,
  Volume2,
  VolumeX
} from 'lucide-react'

import { cn } from '@/lib/utils'

type SpeechAudioData = {
  base64?: string
  mediaType?: string
  format?: string
}

export interface AudioPlayerProps
  extends ComponentProps<'div'> {
  style?: CSSProperties
}

type AudioPlayerContextValue = {
  audioRef: MutableRefObject<HTMLAudioElement | null>
  currentTime: number
  duration: number
  isMuted: boolean
  isPlaying: boolean
  resetPlayback: () => void
  seekBy: (offsetSeconds: number) => void
  seekTo: (time: number) => void
  setDurationHint: (durationSeconds?: number | null) => void
  setMuted: (muted: boolean) => void
  setVolume: (volume: number) => void
  syncAudioState: (audio: HTMLAudioElement) => void
  togglePlayback: () => void
  volume: number
}

const AudioPlayerContext = createContext<AudioPlayerContextValue | null>(null)

const audioRangeClassName = [
  'h-1.5 cursor-pointer appearance-none rounded-full',
  'bg-muted-foreground/20 dark:bg-muted-foreground/35 accent-primary',
  '[&::-webkit-slider-runnable-track]:h-1.5',
  '[&::-webkit-slider-runnable-track]:rounded-full',
  '[&::-webkit-slider-runnable-track]:bg-muted-foreground/20',
  'dark:[&::-webkit-slider-runnable-track]:bg-muted-foreground/35',
  '[&::-webkit-slider-thumb]:-mt-[5px]',
  '[&::-webkit-slider-thumb]:size-4',
  '[&::-webkit-slider-thumb]:appearance-none',
  '[&::-webkit-slider-thumb]:rounded-full',
  '[&::-webkit-slider-thumb]:border',
  '[&::-webkit-slider-thumb]:border-border',
  '[&::-webkit-slider-thumb]:bg-background',
  'dark:[&::-webkit-slider-thumb]:bg-foreground',
  '[&::-moz-range-track]:h-1.5',
  '[&::-moz-range-track]:rounded-full',
  '[&::-moz-range-track]:bg-muted-foreground/20',
  'dark:[&::-moz-range-track]:bg-muted-foreground/35',
  '[&::-moz-range-thumb]:size-4',
  '[&::-moz-range-thumb]:rounded-full',
  '[&::-moz-range-thumb]:border',
  '[&::-moz-range-thumb]:border-border',
  '[&::-moz-range-thumb]:bg-background',
  'dark:[&::-moz-range-thumb]:bg-foreground'
].join(' ')

// --------------

function getAudioDataSource(data?: SpeechAudioData) {
  if (!data?.base64) {
    return undefined
  }

  return `data:${getAudioDataMediaType(data)};base64,${data.base64}`
}

// --------------

function getAudioDataMediaType(data?: SpeechAudioData) {
  return data?.mediaType ?? `audio/${data?.format ?? 'mpeg'}`
}

// --------------

function formatAudioTime(time: number) {
  if (!Number.isFinite(time) || time <= 0) {
    return '0:00'
  }

  const minutes = Math.floor(time / 60)
  const seconds = Math.floor(time % 60)

  return `${minutes}:${seconds.toString().padStart(2, '0')}`
}

// --------------

function useAudioPlayerContext() {
  const context = useContext(AudioPlayerContext)

  if (!context) {
    throw new Error('AudioPlayer controls must be used within AudioPlayer')
  }

  return context
}

// --------------

export function AudioPlayer({
  children,
  className,
  style,
  ...props
}: AudioPlayerProps) {
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [isMuted, setIsMuted] = useState(false)
  const [isPlaying, setIsPlaying] = useState(false)
  const [volume, setVolumeState] = useState(1)

  const resetPlayback = useCallback(() => {
    setCurrentTime(0)
    setDuration(0)
    setIsPlaying(false)
  }, [])

  const syncAudioState = useCallback((audio: HTMLAudioElement) => {
    const nextCurrentTime = audio.currentTime || 0

    setCurrentTime(nextCurrentTime)
    setDuration((previous) =>
      Number.isFinite(audio.duration) && audio.duration > 0
        ? audio.duration
        : Math.max(previous, nextCurrentTime)
    )
    setIsMuted(audio.muted)
    setIsPlaying(!audio.paused)
    setVolumeState(audio.volume)
  }, [])

  const setDurationHint = useCallback((durationSeconds?: number | null) => {
    if (typeof durationSeconds !== 'number' || !Number.isFinite(durationSeconds)) {
      return
    }

    setDuration((previous) => previous > 0 ? previous : Math.max(0, durationSeconds))
  }, [])

  const seekTo = useCallback((time: number) => {
    const audio = audioRef.current

    if (!audio) {
      return
    }

    const nextTime = Math.min(Math.max(time, 0), audio.duration || time)
    audio.currentTime = nextTime
    setCurrentTime(nextTime)
  }, [])

  const seekBy = useCallback(
    (offsetSeconds: number) => {
      const audio = audioRef.current
      seekTo((audio?.currentTime ?? 0) + offsetSeconds)
    },
    [seekTo]
  )

  const togglePlayback = useCallback(() => {
    const audio = audioRef.current

    if (!audio) {
      return
    }

    if (audio.paused) {
      void audio.play().catch(() => setIsPlaying(false))
      return
    }

    audio.pause()
  }, [])

  const setMuted = useCallback((muted: boolean) => {
    const audio = audioRef.current

    if (audio) {
      audio.muted = muted
    }

    setIsMuted(muted)
  }, [])

  const setVolume = useCallback((nextVolume: number) => {
    const clampedVolume = Math.min(Math.max(nextVolume, 0), 1)
    const audio = audioRef.current

    if (audio) {
      audio.volume = clampedVolume
      audio.muted = clampedVolume === 0
    }

    setVolumeState(clampedVolume)
    setIsMuted(clampedVolume === 0)
  }, [])

  const contextValue = useMemo(
    () => ({
      audioRef,
      currentTime,
      duration,
      isMuted,
      isPlaying,
      resetPlayback,
      seekBy,
      seekTo,
      setDurationHint,
      setMuted,
      setVolume,
      syncAudioState,
      togglePlayback,
      volume
    }),
    [
      currentTime,
      duration,
      isMuted,
      isPlaying,
      resetPlayback,
      seekBy,
      seekTo,
      setDurationHint,
      setMuted,
      setVolume,
      syncAudioState,
      togglePlayback,
      volume
    ]
  )

  return (
    <AudioPlayerContext.Provider value={contextValue}>
      <div
        className={cn(
          'not-prose border-border/70 bg-background/80 text-foreground w-full rounded-md border p-2 shadow-sm',
          className
        )}
        style={style}
        {...props}
      >
        {children}
      </div>
    </AudioPlayerContext.Provider>
  )
}

export interface AudioPlayerElementProps
  extends Omit<ComponentProps<'audio'>, 'src'> {
  contentType?: string
  data?: SpeechAudioData
  durationHint?: number | null
  src?: string
}

export const AudioPlayerElement = forwardRef<
  HTMLAudioElement,
  AudioPlayerElementProps
>(({
  className,
  contentType,
  data,
  durationHint,
  onDurationChange,
  onEnded,
  onError,
  onLoadedMetadata,
  onPause,
  onPlay,
  onTimeUpdate,
  preload = 'metadata',
  src,
  ...props
}, ref) => {
  const { audioRef, resetPlayback, setDurationHint, syncAudioState } =
    useAudioPlayerContext()
  const audioSource = src ?? getAudioDataSource(data)
  const audioType = contentType ?? (data ? getAudioDataMediaType(data) : undefined)
  const setAudioElementRef = useCallback(
    (audio: HTMLAudioElement | null) => {
      audioRef.current = audio

      if (typeof ref === 'function') {
        ref(audio)
      } else if (ref) {
        ref.current = audio
      }
    },
    [audioRef, ref]
  )

  useEffect(() => {
    const audio = audioRef.current

    if (!audio) {
      return
    }

    resetPlayback()
    setDurationHint(durationHint)

    try {
      audio.load()
    } catch {
      // jsdom and some old embedded browsers do not implement load().
    }
  }, [audioRef, audioSource, durationHint, resetPlayback, setDurationHint])

  return (
    <audio
      key={audioSource}
      ref={setAudioElementRef}
      className={cn('sr-only', className)}
      preload={preload}
      playsInline
      {...props}
      onDurationChange={(event) => {
        syncAudioState(event.currentTarget)
        onDurationChange?.(event)
      }}
      onEnded={(event) => {
        syncAudioState(event.currentTarget)
        onEnded?.(event)
      }}
      onError={(event) => {
        syncAudioState(event.currentTarget)
        onError?.(event)
      }}
      onLoadedMetadata={(event) => {
        syncAudioState(event.currentTarget)
        onLoadedMetadata?.(event)
      }}
      onPause={(event) => {
        syncAudioState(event.currentTarget)
        onPause?.(event)
      }}
      onPlay={(event) => {
        syncAudioState(event.currentTarget)
        onPlay?.(event)
      }}
      onTimeUpdate={(event) => {
        syncAudioState(event.currentTarget)
        onTimeUpdate?.(event)
      }}
    >
      {audioSource ? (
        <source
          src={audioSource}
          {...(audioType ? { type: audioType } : {})}
        />
      ) : null}
    </audio>
  )
})

AudioPlayerElement.displayName = 'AudioPlayerElement'

export function AudioPlayerControlBar({
  className,
  ...props
}: ComponentProps<'div'>) {
  return (
    <div
      className={cn('flex w-full items-center gap-1', className)}
      {...props}
    />
  )
}

export function AudioPlayerPlayButton({
  className,
  ...props
}: ComponentProps<'button'>) {
  const { isPlaying, togglePlayback } = useAudioPlayerContext()

  return (
    <button
      {...props}
      aria-label={isPlaying ? 'Pause audio' : 'Listen to audio'}
      className={cn(
        'text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:ring-ring flex size-8 shrink-0 items-center justify-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2',
        className
      )}
      type="button"
      onClick={togglePlayback}
    >
      {isPlaying ? (
        <Pause className="size-4" aria-hidden="true" />
      ) : (
        <Volume2 className="size-4" aria-hidden="true" />
      )}
    </button>
  )
}

export function AudioPlayerSeekBackwardButton({
  className,
  seekOffset = 10,
  ...props
}: ComponentProps<'button'> & { seekOffset?: number }) {
  const { seekBy } = useAudioPlayerContext()

  return (
    <button
      {...props}
      aria-label={`Seek back ${seekOffset} seconds`}
      className={cn(
        'text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:ring-ring hidden size-8 shrink-0 items-center justify-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 sm:flex',
        className
      )}
      type="button"
      onClick={() => seekBy(-seekOffset)}
    >
      <RotateCcw className="size-4" aria-hidden="true" />
    </button>
  )
}

export function AudioPlayerSeekForwardButton({
  className,
  seekOffset = 10,
  ...props
}: ComponentProps<'button'> & { seekOffset?: number }) {
  const { seekBy } = useAudioPlayerContext()

  return (
    <button
      {...props}
      aria-label={`Seek forward ${seekOffset} seconds`}
      className={cn(
        'text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:ring-ring hidden size-8 shrink-0 items-center justify-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 sm:flex',
        className
      )}
      type="button"
      onClick={() => seekBy(seekOffset)}
    >
      <RotateCw className="size-4" aria-hidden="true" />
    </button>
  )
}

export function AudioPlayerTimeDisplay({
  className,
  ...props
}: ComponentProps<'span'>) {
  const { currentTime } = useAudioPlayerContext()

  return (
    <span
      className={cn('text-muted-foreground min-w-10 text-xs tabular-nums', className)}
      {...props}
    >
      {formatAudioTime(currentTime)}
    </span>
  )
}

export function AudioPlayerTimeRange({
  className,
  ...props
}: Omit<ComponentProps<'input'>, 'max' | 'min' | 'onChange' | 'type' | 'value'>) {
  const { currentTime, duration, seekTo } = useAudioPlayerContext()

  return (
    <input
      {...props}
      aria-label="Audio position"
      className={cn(
        audioRangeClassName,
        'min-w-20 flex-1',
        className
      )}
      max={duration || 0}
      min={0}
      type="range"
      value={Math.min(currentTime, duration || currentTime)}
      onChange={(event) => seekTo(Number(event.currentTarget.value))}
    />
  )
}

export function AudioPlayerDurationDisplay({
  className,
  ...props
}: ComponentProps<'span'>) {
  const { duration } = useAudioPlayerContext()

  return (
    <span
      className={cn('text-muted-foreground min-w-10 text-xs tabular-nums', className)}
      {...props}
    >
      {formatAudioTime(duration)}
    </span>
  )
}

export function AudioPlayerMuteButton({
  className,
  ...props
}: ComponentProps<'button'>) {
  const { isMuted, setMuted } = useAudioPlayerContext()

  return (
    <button
      {...props}
      aria-label={isMuted ? 'Unmute audio' : 'Mute audio'}
      className={cn(
        'text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:ring-ring hidden size-8 shrink-0 items-center justify-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 sm:flex',
        className
      )}
      type="button"
      onClick={() => setMuted(!isMuted)}
    >
      {isMuted ? (
        <VolumeX className="size-4" aria-hidden="true" />
      ) : (
        <Volume2 className="size-4" aria-hidden="true" />
      )}
    </button>
  )
}

export function AudioPlayerVolumeRange({
  className,
  ...props
}: Omit<ComponentProps<'input'>, 'max' | 'min' | 'onChange' | 'step' | 'type' | 'value'>) {
  const { setVolume, volume } = useAudioPlayerContext()

  return (
    <input
      {...props}
      aria-label="Audio volume"
      className={cn(
        audioRangeClassName,
        'hidden w-16 sm:block',
        className
      )}
      max={1}
      min={0}
      step={0.05}
      type="range"
      value={volume}
      onChange={(event) => setVolume(Number(event.currentTarget.value))}
    />
  )
}
